/**
 * Roles tab displaying role cards with management actions
 */
import { Plus, Shield, Trash2 } from "lucide-react";
import { useState } from "react";

import { RoleDetailsSheet } from "./RoleDetailsSheet";

import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card";
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Textarea } from "@/components/ui/textarea";
import { trpc } from "@/lib/trpc";


/**
 * Roles tab displaying role cards with management actions
 */
export function RolesTab() {
	const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
	const [newRoleName, setNewRoleName] = useState("");
	const [newRoleSlug, setNewRoleSlug] = useState("");
	const [newRoleDescription, setNewRoleDescription] = useState("");
	const [selectedRole, setSelectedRole] = useState<string | null>(null);

	// Fetch roles
	const rolesQuery = trpc.roles.list.useQuery();

	// Create role mutation
	const createRoleMutation = trpc.roles.create.useMutation({
		onSuccess: () => {
			void rolesQuery.refetch();
			setIsCreateDialogOpen(false);
			setNewRoleName("");
			setNewRoleSlug("");
			setNewRoleDescription("");
		},
	});

	// Delete role mutation
	const deleteRoleMutation = trpc.roles.delete.useMutation({
		onSuccess: () => {
			void rolesQuery.refetch();
		},
	});

	/**
	 * Generates a slug from the role name
	 * @param name - The role name to convert to a slug
	 */
	const generateSlug = (name: string) => {
		return name
			.toLowerCase()
			.replace(/[^a-z0-9]+/g, "-")
			.replace(/^-|-$/g, "");
	};

	/**
	 * Handles name change and auto-generates slug
	 * @param name - The new role name
	 */
	const handleNameChange = (name: string) => {
		setNewRoleName(name);
		// Auto-generate slug if slug hasn't been manually edited
		if (!newRoleSlug || newRoleSlug === generateSlug(newRoleName)) {
			setNewRoleSlug(generateSlug(name));
		}
	};

	/**
	 * Creates a new role
	 */
	const handleCreateRole = () => {
		if (!newRoleName.trim() || !newRoleSlug.trim()) return;

		createRoleMutation.mutate({
			name: newRoleName.trim(),
			slug: newRoleSlug.trim(),
			description: newRoleDescription.trim() || undefined,
		});
	};

	/**
	 * Deletes a role after confirmation
	 * @param roleId - The ID of the role to delete
	 */
	const handleDeleteRole = (roleId: string) => {
		if (window.confirm("Are you sure you want to delete this role?")) {
			deleteRoleMutation.mutate({ roleId });
		}
	};

	const selectedRoleData = rolesQuery.data?.find((r) => r.id === selectedRole);

	return (
		<div className="space-y-4">
			{/* Header */}
			<div className="flex items-center justify-end">
				<Button onClick={() => { setIsCreateDialogOpen(true); }}>
					<Plus className="mr-2 h-4 w-4" />
					Create Role
				</Button>
			</div>

			{/* Roles Grid */}
			{rolesQuery.isLoading ? (
				<div className="flex items-center justify-center py-8">
					<div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
				</div>
			) : rolesQuery.data?.length === 0 ? (
				<Card>
					<CardContent className="flex flex-col items-center justify-center py-12">
						<Shield className="h-12 w-12 text-muted-foreground mb-4" />
						<h3 className="text-lg font-medium mb-2">No Roles</h3>
						<p className="text-muted-foreground text-center max-w-md mb-4">
							Create custom roles to define different permission levels for users.
						</p>
						<Button onClick={() => { setIsCreateDialogOpen(true); }}>
							<Plus className="mr-2 h-4 w-4" />
							Create First Role
						</Button>
					</CardContent>
				</Card>
			) : (
				<div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
					{rolesQuery.data?.map((role) => (
						<Card
							key={role.id}
							className="cursor-pointer hover:border-primary/50 transition-colors"
							onClick={() => { setSelectedRole(role.id); }}
						>
							<CardHeader className="pb-3">
								<div className="flex items-start justify-between">
									<div className="flex items-center gap-2">
										<Shield className="h-5 w-5 text-muted-foreground" />
										<CardTitle className="text-lg">{role.name}</CardTitle>
									</div>
									{!role.isSystem && (
										<Button
											variant="ghost"
											size="icon"
											onClick={(e) => {
												e.stopPropagation();
												handleDeleteRole(role.id);
											}}
											disabled={deleteRoleMutation.isPending}
										>
											<Trash2 className="h-4 w-4 text-destructive" />
										</Button>
									)}
								</div>
								{role.description && (
									<CardDescription>{role.description}</CardDescription>
								)}
							</CardHeader>
							<CardContent>
								<div className="space-y-3">
									<div className="flex items-center justify-between text-sm">
										<span className="text-muted-foreground">Slug</span>
										<span className="font-mono text-xs bg-muted px-2 py-0.5 rounded">
											{role.slug}
										</span>
									</div>
									<div className="flex items-center justify-between text-sm">
										<span className="text-muted-foreground">Capabilities</span>
										<span className="font-medium">{role.capabilities.length}</span>
									</div>
									{role.isSystem && (
										<div className="text-xs text-muted-foreground bg-muted px-2 py-1 rounded">
											System role (cannot be deleted)
										</div>
									)}
									<div className="pt-2">
										<div className="text-xs text-muted-foreground mb-1">Capabilities:</div>
										<div className="flex flex-wrap gap-1">
											{role.capabilities.length === 0 ? (
												<span className="text-xs text-muted-foreground">None</span>
											) : (
												role.capabilities.slice(0, 3).map((cap) => (
													<span
														key={cap}
														className="text-xs bg-secondary px-2 py-0.5 rounded"
													>
														{cap}
													</span>
												))
											)}
											{role.capabilities.length > 3 && (
												<span className="text-xs text-muted-foreground">
													+{role.capabilities.length - 3} more
												</span>
											)}
										</div>
									</div>
								</div>
							</CardContent>
						</Card>
					))}
				</div>
			)}

			{/* Create Role Dialog */}
			<Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>Create Role</DialogTitle>
						<DialogDescription>
							Create a new role to define permissions for users.
						</DialogDescription>
					</DialogHeader>
					<form
						onSubmit={(e) => {
							e.preventDefault();
							handleCreateRole();
						}}
					>
						<div className="space-y-4 py-4">
							<div className="space-y-2">
								<Label htmlFor="name">Role Name</Label>
								<Input
									id="name"
									value={newRoleName}
									onChange={(e) => { handleNameChange(e.target.value); }}
									placeholder="e.g., Marketing Manager"
								/>
							</div>
							<div className="space-y-2">
								<Label htmlFor="slug">Slug</Label>
								<Input
									id="slug"
									value={newRoleSlug}
									onChange={(e) => { setNewRoleSlug(e.target.value); }}
									placeholder="e.g., marketing-manager"
									pattern="^[a-z0-9-]+$"
								/>
								<p className="text-xs text-muted-foreground">
									Lowercase letters, numbers, and hyphens only
								</p>
							</div>
							<div className="space-y-2">
								<Label htmlFor="description">Description (optional)</Label>
								<Textarea
									id="description"
									value={newRoleDescription}
									onChange={(e) => { setNewRoleDescription(e.target.value); }}
									placeholder="What permissions should this role have?"
									rows={3}
								/>
							</div>

							{/* Error */}
							{createRoleMutation.isError && (
								<div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">
									{createRoleMutation.error.message}
								</div>
							)}
						</div>
						<DialogFooter>
							<Button
								type="button"
								variant="outline"
								onClick={() => { setIsCreateDialogOpen(false); }}
							>
								Cancel
							</Button>
							<Button
								type="submit"
								disabled={!newRoleName.trim() || !newRoleSlug.trim() || createRoleMutation.isPending}
							>
								{createRoleMutation.isPending ? "Creating..." : "Create Role"}
							</Button>
						</DialogFooter>
					</form>
				</DialogContent>
			</Dialog>

			{/* Delete Error */}
			{deleteRoleMutation.isError && (
				<div className="fixed bottom-4 right-4 rounded-md bg-destructive/10 border border-destructive/20 p-3 text-sm text-destructive max-w-sm">
					{deleteRoleMutation.error.message}
				</div>
			)}

			{/* Role Details Sheet */}
			<Sheet open={!!selectedRole} onOpenChange={(open) => { if (!open) setSelectedRole(null); }}>
				<SheetContent className="sm:max-w-lg overflow-y-auto">
					{selectedRoleData && (
						<RoleDetailsSheet
							roleId={selectedRoleData.id}
							onClose={() => { setSelectedRole(null); }}
							onUpdate={() => { void rolesQuery.refetch(); }}
						/>
					)}
				</SheetContent>
			</Sheet>
		</div>
	);
}
